/**
 * Minimal Markdown → HTML for in-app help (headings, tables, lists, code, bold).
 * Not a full MD parser — keep help docs simple.
 */
export function markdownLiteToHtml(source: string): string {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (/^```/.test(line)) {
      const fence = line.trim();
      i += 1;
      const block: string[] = [];
      while (i < lines.length && !lines[i].startsWith('```')) {
        block.push(lines[i]);
        i += 1;
      }
      if (i < lines.length) i += 1;
      out.push(`<pre class="md-code-block"><code>${escapeHtml(block.join('\n'))}</code></pre>`);
      continue;
    }

    if (/^\|.+\|$/.test(line.trim())) {
      const tableLines: string[] = [];
      while (i < lines.length && /^\|.+\|$/.test(lines[i].trim())) {
        tableLines.push(lines[i].trim());
        i += 1;
      }
      out.push(renderTable(tableLines));
      continue;
    }

    if (/^---+$/.test(line.trim())) {
      out.push('<hr class="md-hr" />');
      i += 1;
      continue;
    }

    const h3 = line.match(/^###\s+(.+)$/);
    if (h3) {
      out.push(`<h3 class="md-h3">${inlineFormat(h3[1])}</h3>`);
      i += 1;
      continue;
    }

    const h2 = line.match(/^##\s+(.+)$/);
    if (h2) {
      out.push(`<h2 class="md-h2">${inlineFormat(h2[1])}</h2>`);
      i += 1;
      continue;
    }

    const h1 = line.match(/^#\s+(.+)$/);
    if (h1) {
      out.push(`<h1 class="md-h1">${inlineFormat(h1[1])}</h1>`);
      i += 1;
      continue;
    }

    if (/^-\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^-\s+/.test(lines[i])) {
        items.push(`<li>${inlineFormat(lines[i].replace(/^-\s+/, ''))}</li>`);
        i += 1;
      }
      out.push(`<ul class="md-ul">${items.join('')}</ul>`);
      continue;
    }

    if (line.trim() === '') {
      i += 1;
      continue;
    }

    out.push(`<p class="md-p">${inlineFormat(line)}</p>`);
    i += 1;
  }

  return out.join('\n');
}

function renderTable(tableLines: string[]): string {
  if (!tableLines.length) return '';
  const parseRow = (row: string) =>
    row
      .slice(1, -1)
      .split('|')
      .map((c) => c.trim());

  const header = parseRow(tableLines[0]);
  let bodyStart = 1;
  if (
    tableLines.length > 1 &&
    tableLines[1].replace(/[|\s:-]/g, '') === ''
  ) {
    bodyStart = 2;
  }

  const headHtml = `<thead><tr>${header.map((c) => `<th>${inlineFormat(c)}</th>`).join('')}</tr></thead>`;
  const rows = tableLines.slice(bodyStart).map((row) => {
    const cells = parseRow(row);
    return `<tr>${cells.map((c) => `<td>${inlineFormat(c)}</td>`).join('')}</tr>`;
  });
  return `<table class="table table-sm table-bordered md-table"><${headHtml}<tbody>${rows.join('')}</tbody></table>`;
}

function inlineFormat(text: string): string {
  let s = escapeHtml(text);
  s = s.replace(/`([^`]+)`/g, '<code class="md-code">$1</code>');
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  return s;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
